import type {
  CommunicationLogFilter,
  FileListFilter,
  FlowisDocumentQuery,
  MappingKind,
  OperationListFilter,
} from './types';

/** The cache segment for a tenant; distinct from any GUID so an unselected state never collides. */
const tenant = (tenantGid: string | null) => tenantGid ?? 'none';

/**
 * One place for every cache key, so invalidation can target a whole family (`files.all`) or one
 * entry without a caller having to remember the shape. Tenant-scoped families carry the selected
 * tenant as their first segment after the family name: a late response from before a switch then
 * settles under the old tenant's keys instead of the visible ones.
 */
export const queryKeys = {
  /** Session-level: who the API decided the caller is. Fetched once, read by every page. */
  caller: {
    all: ['caller'] as const,
    context: () => ['caller', 'context'] as const,
  },
  accounts: {
    all: ['accounts'] as const,
    list: (tenantGid: string | null) => ['accounts', tenant(tenantGid), 'list'] as const,
  },
  files: {
    all: ['files'] as const,
    list: (tenantGid: string | null, filter: FileListFilter) =>
      ['files', tenant(tenantGid), 'list', filter] as const,
    detail: (tenantGid: string | null, fileGid: string) =>
      ['files', tenant(tenantGid), 'detail', fileGid] as const,
  },
  operations: {
    all: ['operations'] as const,
    list: (tenantGid: string | null, filter: OperationListFilter) =>
      ['operations', tenant(tenantGid), 'list', filter] as const,
    detail: (tenantGid: string | null, operationGid: string) =>
      ['operations', tenant(tenantGid), 'detail', operationGid] as const,
  },
  // Tenant-scoped like the rest, though an administrator reads across tenants with none selected:
  // 'none' is then simply the segment their cross-tenant listing settles under.
  communicationLog: {
    all: ['communication-log'] as const,
    list: (tenantGid: string | null, filter: CommunicationLogFilter) =>
      ['communication-log', tenant(tenantGid), 'list', filter] as const,
    detail: (tenantGid: string | null, entryGid: string) =>
      ['communication-log', tenant(tenantGid), 'detail', entryGid] as const,
  },
  mappings: {
    all: ['mappings'] as const,
    list: (tenantGid: string | null, type: MappingKind | undefined) =>
      ['mappings', tenant(tenantGid), 'list', type ?? 'all'] as const,
  },
  standardCodes: {
    all: ['standard-codes'] as const,
    // Intentionally tenant-free: the codes are the same for everybody and the route is [AllowsNoTenant].
    byKind: (type: MappingKind) => ['standard-codes', type] as const,
  },
  // The shared code list registry: versions, entries and the active list are the same for every
  // tenant. Only the affected-mapping reads (diff, broken) depend on the selected tenant, because
  // the mappings they name are tenant data.
  codelists: {
    all: ['codelists'] as const,
    active: (kind: MappingKind) => ['codelists', 'active', kind] as const,
    versions: (kind?: MappingKind) => ['codelists', 'versions', kind ?? 'all'] as const,
    version: (versionGid: string) => ['codelists', 'version', versionGid] as const,
    diff: (tenantGid: string | null, versionGid: string) =>
      ['codelists', tenant(tenantGid), 'diff', versionGid] as const,
    broken: (tenantGid: string | null, kind?: MappingKind) =>
      ['codelists', tenant(tenantGid), 'broken', kind ?? 'all'] as const,
  },
  inbound: {
    all: ['inbound'] as const,
    pending: (tenantGid: string | null, accountGid: string | undefined) =>
      ['inbound', tenant(tenantGid), 'pending', accountGid ?? 'all'] as const,
    waiting: (tenantGid: string | null, accountGid: string | undefined) =>
      ['inbound', tenant(tenantGid), 'waiting', accountGid ?? 'all'] as const,
  },
  flowis: {
    all: ['flowis'] as const,
    // Intentionally tenant-free: FlowisController marks these global endpoints [AllowsNoTenant].
    availability: () => ['flowis', 'availability'] as const,
    peppolLookup: (participantId: string) => ['flowis', 'peppol-lookup', participantId] as const,
    account: (tenantGid: string | null, accountGid: string) =>
      ['flowis', tenant(tenantGid), accountGid] as const,
    sent: (tenantGid: string | null, accountGid: string, query: FlowisDocumentQuery) =>
      ['flowis', tenant(tenantGid), accountGid, 'sent', query] as const,
    sentDetail: (tenantGid: string | null, accountGid: string, documentId: string) =>
      ['flowis', tenant(tenantGid), accountGid, 'sent', 'detail', documentId] as const,
    received: (tenantGid: string | null, accountGid: string, query: FlowisDocumentQuery) =>
      ['flowis', tenant(tenantGid), accountGid, 'received', query] as const,
    receivedDetail: (tenantGid: string | null, accountGid: string, documentId: string) =>
      ['flowis', tenant(tenantGid), accountGid, 'received', 'detail', documentId] as const,
  },
  admin: {
    all: ['admin'] as const,
    tenants: () => ['admin', 'tenants'] as const,
    tenant: (tenantGid: string) => ['admin', 'tenants', tenantGid] as const,
    tenantHealth: (tenantGid: string) => ['admin', 'tenants', tenantGid, 'health'] as const,
    accounts: (unassigned?: boolean) => ['admin', 'accounts', unassigned ?? 'all'] as const,
    remoteAccounts: () => ['admin', 'accounts', 'remote'] as const,
  },
} as const;
